// src/components/MultiBucketModal/MultiBucketSelectForm.js
import React, { useMemo, useState } from 'react';
import { Grid, Button, InputBase, Divider, TextField } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import { IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { makeStyles } from '@mui/styles';
import styles from '../Datepicker/style'; // reuse the exact same classnames as ExporterModal
import { AiFillCloseSquare } from 'react-icons/ai';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { PaginationController } from 'components';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(styles);

const SearchBar = styled(InputBase)(({ theme }) => ({
  fontFamily: 'Montserrat-Light',
  fontSize: 15,
  fontWeight: 300,
  width: '100%',
  height: '100%',
  maxHeight: 59,
  borderRadius: 15,
  backgroundColor: '#F3F3F4',
  padding: '20px',
  color: '#36333D',
  [theme.breakpoints.down('xl')]: {
    padding: 15,
    fontSize: 13,
    fontWeight: 300,
    maxHeight: 45,
  },
}));

const BUCKETS_PER_PAGE = 10;
const MAX_BUCKETS = 5; // must match MultiBucketMixin.MAX_BUCKETS on the backend

// Same 5-year-back / no-future bound as everywhere else in the analytics
// section (Dashboard.js, CombinedAnalyticsDashboard.js, the exporter's own
// custom date fields) — this form's two date inputs had no bound at all
// before, so any date, past or future, could be typed in freely.
function isoDate(d) {
  return d.toISOString().slice(0, 10);
}
const DATE_INPUT_MAX = isoDate(new Date());
const DATE_INPUT_MIN = isoDate(new Date(new Date().setFullYear(new Date().getFullYear() - 5)));

/**
 * props:
 *  - buckets: full bucket list (array of {code, name, company_name, ...}) — already
 *             loaded on the page via getAllAnalytics, so no extra fetch is needed here.
 *  - selectedCodes: array of currently selected bucket codes
 *  - onToggleCode(code): fn
 *  - onSelectAll(): fn - selects up to MAX_BUCKETS from current filtered list
 *  - onClearAll(): fn
 *  - dateType, onDateTypeChange, startDate, endDate, onStartDateChange, onEndDateChange:
 *    still accepted for backward compatibility with the parent, but this
 *    form no longer renders a Date Type UI — the parent now supplies a
 *    fixed default (see XDataAnalyticsBuckets.js) instead of asking the
 *    user to pick one before combining buckets.
 *  - onClose, onSubmit
 */
export default function MultiBucketSelectForm({
  buckets = [],
  selectedCodes = [],
  onToggleCode,
  onSelectAll,
  onClearAll,
  dateType,
  onDateTypeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onClose,
  onSubmit,
}) {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);

  const filteredBuckets = useMemo(() => {
    if (!searchText) return buckets;
    const s = searchText.toLowerCase();
    // move on to next / never throw: guard every field access
    return (buckets || []).filter((b) => {
      const code = (b?.code || '').toLowerCase();
      const name = (b?.name || '').toLowerCase();
      return code.includes(s) || name.includes(s);
    });
  }, [buckets, searchText]);

  const totalPages = Math.max(1, Math.ceil((filteredBuckets.length || 0) / BUCKETS_PER_PAGE));
  const pageStart = (page - 1) * BUCKETS_PER_PAGE;
  const pagedBuckets = filteredBuckets.slice(pageStart, pageStart + BUCKETS_PER_PAGE);

  const limitReached = selectedCodes.length >= MAX_BUCKETS;

  const handleSearchChange = (e) => {
    setPage(1);
    setSearchText(e.target.value);
  };

  const handleCheck = (code) => () => {
    // allow un-checking always; block new checks past MAX_BUCKETS
    if (selectedCodes.includes(code) || !limitReached) {
      onToggleCode(code);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <div className={classes.head}>
        <div className={classes.text}>{t('MultiBucketAnalytics.title')}</div>
        <div className={classes.button}>
          <IconButton onClick={onClose}>
            <AiFillCloseSquare color="#C8102E" size="30px" />
          </IconButton>
        </div>
      </div>
      <Divider />

      <Grid className={classes.grid} container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={8}>
          <InputLabel className={classes.inputLabel}>{t('MultiBucketAnalytics.bucketCode')}</InputLabel>
          <SearchBar
            placeholder={t('MultiBucketAnalytics.searchBucket')}
            value={searchText}
            onChange={handleSearchChange}
          />
        </Grid>
        <Grid item xs={4} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedCodes.length > 0 && selectedCodes.length === Math.min(filteredBuckets.length, MAX_BUCKETS)}
                indeterminate={selectedCodes.length > 0 && selectedCodes.length < Math.min(filteredBuckets.length, MAX_BUCKETS)}
                style={{ color: '#C8102E' }}
                onChange={(e) => (e.target.checked ? onSelectAll(filteredBuckets) : onClearAll())}
              />
            }
            label={t('MultiBucketAnalytics.selectAll')}
          />
        </Grid>

        <Grid item xs={12} style={{ fontSize: 13, color: limitReached ? '#C8102E' : '#696969' }}>
          {t('MultiBucketAnalytics.selectedCount', { count: selectedCodes.length, max: MAX_BUCKETS })}
        </Grid>

        <Grid item xs={12}>
          <div style={{ minHeight: 220, maxHeight: 260, overflowY: 'auto', border: '2px solid #EAEAEA', borderRadius: 5, padding: 10 }}>
            <Grid container>
              {/* If a thing is missing (no code/name), just skip that row — never throw */}
              {pagedBuckets.map((bucket, idx) => {
                if (!bucket || !bucket.code) return null;
                const checked = selectedCodes.includes(bucket.code);
                return (
                  <Grid item xs={4} key={bucket.code || idx}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          disabled={!checked && limitReached}
                          onChange={handleCheck(bucket.code)}
                          style={{ color: '#C8102E' }}
                        />
                      }
                      label={`${bucket.code}${bucket.name ? ' - ' + bucket.name : ''}`}
                    />
                  </Grid>
                );
              })}
              {pagedBuckets.length === 0 && (
                <Grid item xs={12} style={{ textAlign: 'center', padding: 20, color: '#696969' }}>
                  {t('MultiBucketAnalytics.noBuckets')}
                </Grid>
              )}
            </Grid>
          </div>
          {totalPages > 1 && (
            <div style={{ position: 'relative', height: 60 }}>
              <PaginationController totalCount={totalPages} page={page} loadPagination={setPage} />
            </div>
          )}
        </Grid>
      </Grid>

      <div className={classes.button1} style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={selectedCodes.length === 0}
          style={{ background: '#c8102e' }}
        >
          {t('MultiBucketAnalytics.export')}
        </Button>
      </div>
    </form>
  );
}